import { useState, type FormEvent, type KeyboardEvent } from 'react'
import { Paperclip, SendHorizontal } from 'lucide-react'
import { Button } from '@/components/ds2'
import { useGeneralAgentChatStore } from '../hooks/generalAgentChatStore'
import { AttachPanel } from './uploads/AttachPanel'

interface GeneralAgentComposerProps {
    onSend: (text: string) => void
}

export const GeneralAgentComposer = ({ onSend }: GeneralAgentComposerProps) => {
    const [text, setText] = useState('')
    const [attaching, setAttaching] = useState(false)
    const notify = useGeneralAgentChatStore((s) => s.notify)

    const submit = (event?: FormEvent) => {
        event?.preventDefault()
        if (!text.trim()) return
        onSend(text)
        setText('')
    }

    // Enter sends, Shift+Enter breaks the line; an Enter that picks an IME candidate only finishes the word.
    const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) submit(event)
    }

    return (
        <div className="flex flex-col gap-2">
            {/* After an upload the chat says what was filed, so the agent reads the load again (FR-D2): held while a question
                or a turn waits, so it never withdraws an approval the user has not answered. */}
            {attaching && <AttachPanel onUploaded={notify} onClose={() => setAttaching(false)} />}
            <form onSubmit={submit} className="flex items-end gap-2">
                <Button
                    type="button"
                    variant="ghost"
                    iconOnly
                    aria-label="Attach a file"
                    aria-expanded={attaching}
                    onClick={() => setAttaching((open) => !open)}
                >
                    <Paperclip className="h-4 w-4" />
                </Button>
                <div className="flex flex-1 items-center rounded-xl bg-surface px-3 py-2 shadow-field transition-shadow duration-150 focus-within:shadow-none focus-within:ring-2 focus-within:ring-accent">
                    <textarea
                        aria-label="Message"
                        rows={1}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="Message the general agent"
                        className="max-h-40 w-full resize-none bg-transparent text-sm text-foreground outline-none placeholder:text-foreground-muted"
                    />
                </div>
                <Button type="submit" iconOnly aria-label="Send" disabled={!text.trim()}>
                    <SendHorizontal className="h-4 w-4" />
                </Button>
            </form>
        </div>
    )
}
